import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { LoyaltyDiscountsStore } from 'app/_components/loyalty-discounts/loyalty-discounts.store';
import { APPLICATION_NAME } from 'app/app.constants';
import { Branch } from 'entities/branch';
import { Part } from 'entities/part';
import { Refinement } from 'entities/part-search';
import { PartSearchResponse } from 'entities/parts/part-search-response';
import { Observable, Subject, takeUntil } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { AuthorizationService } from 'services/authorization.service';
import { SourceLocationType } from 'services/logger.service';
import { AppState } from 'store/app-state';
import { OpeningFrom } from 'store/back-counter/back-counter.entities';
import * as BranchSelectors from 'store/branch/branch.selectors';
import * as CartActions from 'store/cart/cart.actions';
import * as LoyaltySelectors from 'store/loyalty/loyalty.selectors';
import { PartBasePortableComponentStore } from './part-base-portable.component.store';

@Component({
    selector: 'part-base-portable',
    templateUrl: './part-base-portable.component.html',
    styleUrls: ['./part-base-portable.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    providers: [PartBasePortableComponentStore, LoyaltyDiscountsStore],
    standalone: false
})
export class PartBasePortableComponent implements OnInit, OnChanges, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  
  @Input() initialSearchTerm?: string;
  @Input() initialPartId?: string;
  @Input() initialPageNumber?: number;
  @Input() isInModal: boolean = false;
  @Input() displayType: string = '';
  @Input() branchCode?: string;
  @Input() rewardNumber?: string;
  @Input() openingFrom?: OpeningFrom;
  @Output() partSelected = new EventEmitter<Part>();
  @Output() partAddedToRequest = new EventEmitter<Part>();
  @Output() navigationOccurred = new EventEmitter<{ searchTerm: string, partId: string, pageNumber: number }>();
  @Output() viewChanged = new EventEmitter<'list' | 'details' | 'loading'>();

  pageSize: number = this.componentStore.pageSize;
  partSearchTerm$: Observable<string> = this.componentStore.queryTerm$;
  currentPage$: Observable<number> = this.componentStore.pageNumber$;
  parts$: Observable<PartSearchResponse> = this.componentStore.result$;
  partCount$: Observable<number> = this.componentStore.count$;
  loading$: Observable<boolean> = this.componentStore.loading$;
  showCreatePart$: Observable<boolean> = this.componentStore.showCreatePart$;
  facets$: Observable<Refinement[]> = this.componentStore.facets$;

  showCreatePart: boolean = false;
  showCreatePartLoader: boolean = false;

  public branch$: Observable<Branch>;

  activeBranchCode: string = "";

  loyaltyAccount$ = this.store.select(LoyaltySelectors.loyaltyAccount).pipe(tap(t => {
    // Trigger the initial part search using current query state in the store
    this.componentStore.triggerSearch();
  }));

  constructor(
    private readonly store: Store<AppState>,
    private readonly componentStore: PartBasePortableComponentStore,
    private readonly loyaltyDiscountsStore: LoyaltyDiscountsStore,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    public authorizationService: AuthorizationService,
    title: Title,
    @Inject(APPLICATION_NAME) appName: string
  ) {
    if (!this.isInModal) {
      title.setTitle(`${appName} - Parts`);
      this.store.dispatch(CartActions.getCartDataForBranchAndCustomer());
    }
  }

  ngOnInit() {
    
    // Use override branch if provided, otherwise use store
    if (this.branchCode) {
      this.branch$ = this.store.select(BranchSelectors.selectedBranch).pipe(
        map(storeBranch => ({
          ...storeBranch,
          code: this.branchCode
        }))
      ).pipe(tap(t => this.activeBranchCode = t.code));
    } else {
      this.branch$ = this.store.select(BranchSelectors.selectedBranch).pipe(tap(t => this.activeBranchCode = t.code));
    }
    
    this.componentStore.init(
      this.isInModal,
      this.initialSearchTerm,
      this.initialPartId,
      this.isInModal ? null : this.activatedRoute,
      this.initialPageNumber,
      this.openingFrom
    );
    this.loyaltyDiscountsStore.getDiscounts(
      this.componentStore.result$.pipe(map((result: PartSearchResponse) => result?.parts ?? []))
    );
    
    // Emit view changes when in modal mode
    if (this.isInModal) {
      this.partCount$.pipe(takeUntil(this.destroy$)).subscribe(count => {
        if (count === 1) {
          this.viewChanged.emit('details');
        } else if (count > 1) {
          this.viewChanged.emit('list');
        }
      });
      
      this.loading$.pipe(takeUntil(this.destroy$)).subscribe(loading => {
        if (loading) {
          this.viewChanged.emit('loading');
        }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When in modal mode and search term or partId changes, trigger a new search
    if (this.isInModal && !changes['initialSearchTerm']?.firstChange && !changes['initialPartId']?.firstChange) {
      if (changes['initialSearchTerm'] || changes['initialPartId']) {
        this.componentStore.init(
          this.isInModal,
          this.initialSearchTerm,
          this.initialPartId,
          null,
          this.initialPageNumber
        );
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  updatePage(page: number) {
    if (this.isInModal) {
      this.componentStore.updatePage(page);
    } else {
      this.router.navigate(['/parts'], {
        queryParams: {
          page: page
        },
        queryParamsHandling: "merge",
        onSameUrlNavigation: "ignore"
      } as NavigationExtras);
    }
  }

  updateFacets(facets: Refinement[]) {
    this.componentStore.updateFacets(facets);
  }

  goToPart(part: Part | string) {
    if (this.isInModal) {
      // In modal mode, navigate to part details view within the modal
      // Emit current state before navigation including page number
      let currentPageNumber = 1;
      this.currentPage$.pipe(take(1)).subscribe(page => currentPageNumber = page);
      
      this.navigationOccurred.emit({ 
        searchTerm: this.initialSearchTerm || '', 
        partId: this.initialPartId || '',
        pageNumber: currentPageNumber
      });
      
      if (typeof part === 'string') {
        // String-based search term
        this.componentStore.searchForPart({ term: part, partId: '' });
      } else {
        // Part object - search using both rushPartNumber and partId (if available)
        const partId = part.partId || '';
        const searchTerm = part.rushPartNumber || part.number || '';
        this.componentStore.searchForPart({ term: searchTerm, partId: partId });
      }
    } else {
      // In route mode, navigate to the part
      const partQueryParams = typeof part === 'string'
        ? { searchTerm: part }
        : { searchTerm: part.rushPartNumber, partId: part.partId };

      return this.router.navigate(['/parts'], {
        queryParams: {
          ...partQueryParams,
          src: SourceLocationType.PartListResult
        }
      } as NavigationExtras);
    }
  }

  selectPartFromDetails(part: Part): void {
    // When part-details emits a selection, pass it up
    this.partSelected.emit(part);
  }

  onPartAddedToRequest(part: Part): void {
    // When a part is added to request from part-list, emit it
    this.partAddedToRequest.emit(part);
  }

  dismissCreatePart(): void {
    this.componentStore.dismissCreatePart();
  }

  clearFacets(): void {
    this.componentStore.clearFacets();
  }
}
